<!-- SPDX-License-Identifier: GPL-3.0-or-later -->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import RoomTemplate from '../templates/RoomTemplate.svelte';
  import RoomHeader from '../organisms/RoomHeader.svelte';
  import RoomControls from '../organisms/RoomControls.svelte';
  import ParticipantGrid from '../organisms/ParticipantGrid.svelte';
  import { RoomViewModel } from '$lib/viewmodels/RoomViewModel.svelte';
  import { BrowserMediaService } from '$lib/services/MediaService';
  import { WebAudioLevelService } from '$lib/services/AudioLevelService';
  import { HtmlRemoteAudioSinkService } from '$lib/services/RemoteAudioSinkService';
  import { PeerJsSignalingService } from '$lib/services/SignalingService';
  import { HttpTurnCredentialsClient } from '$lib/services/TurnCredentialsClient';
  import { loadClientConfig } from '$lib/config';

  interface Props {
    userName: string;
    roomName: string;
    onLeave: () => void;
  }

  let { userName, roomName, onLeave }: Props = $props();

  const cfg = loadClientConfig();
  const media = new BrowserMediaService();
  const audioLevels = new WebAudioLevelService();
  const remoteAudio = new HtmlRemoteAudioSinkService();
  const credentials = new HttpTurnCredentialsClient({ baseUrl: cfg.credentialsBaseUrl });

  let vm = $state<RoomViewModel | null>(null);

  onMount(async () => {
    const iceServers = await credentials.fetchIceServers();
    const signaling = new PeerJsSignalingService({
      host: cfg.signalingHost,
      port: cfg.signalingPort,
      secure: cfg.signalingSecure,
      path: cfg.signalingPath,
      iceServers,
    });
    const model = new RoomViewModel({
      userName,
      roomName,
      media,
      signaling,
      audioLevels,
      remoteAudio,
    });
    vm = model;
    await model.start();
  });

  onDestroy(async () => {
    await vm?.leave();
  });

  async function handleLeave() {
    await vm?.leave();
    onLeave();
  }
</script>

<RoomTemplate>
  {#snippet header()}
    <RoomHeader
      {roomName}
      status={vm?.status ?? 'initializing'}
      peerCount={(vm ? Object.keys(vm.peers).length : 0) + 1}
      copied={vm?.copied ?? false}
      onCopy={() => vm?.copyRoomName()}
    />
  {/snippet}
  {#snippet errorBanner()}
    {#if vm?.error}
      <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
        {vm.error}
      </div>
    {/if}
  {/snippet}
  {#snippet emptyState()}
    {#if vm?.status === 'connected' && Object.keys(vm.peers).length === 0}
      <div
        class="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-indigo-200 text-sm text-center"
      >
        Здесь пока никого нет. Поделитесь именем комнаты
        <span class="font-semibold text-indigo-100">{roomName}</span>, чтобы собрать друзей.
      </div>
    {/if}
  {/snippet}
  {#snippet body()}
    <ParticipantGrid
      me={{ name: userName, audioLevel: vm?.myLevel ?? 0, muted: vm?.muted ?? false }}
      peers={vm?.peers ?? {}}
    />
  {/snippet}
  {#snippet controls()}
    <RoomControls
      muted={vm?.muted ?? false}
      disabled={vm?.status !== 'connected'}
      onToggleMute={() => vm?.toggleMute()}
      onLeave={handleLeave}
    />
  {/snippet}
</RoomTemplate>
